// ═══════════════════════════════════════════════════════════════════════════════
// Online / offline + drain IndexedDB queue when reconnecting
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOfflineQueueLength } from '../offline/offlineQueue';
import { processOfflineQueue } from '../offline/processOfflineQueue';

type SyncState = 'idle' | 'syncing' | 'done' | 'error';

type OfflineSyncContextValue = {
  isOnline: boolean;
  syncState: SyncState;
  syncMessage: string | null;
  pendingCount: number;
  refreshPendingCount: () => Promise<void>;
  runSync: () => Promise<void>;
};

const OfflineSyncContext = createContext<OfflineSyncContextValue | null>(null);

export const OfflineSyncProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPendingCount = useCallback(async () => {
    const n = await getOfflineQueueLength();
    setPendingCount(n);
  }, []);

  const runSync = useCallback(async () => {
    if (!navigator.onLine) {
      return;
    }
    setSyncState('syncing');
    setSyncMessage(null);
    const result = await processOfflineQueue();
    await refreshPendingCount();
    if (result.failedId) {
      setSyncState('error');
      setSyncMessage(result.failedMessage ?? 'Sync failed');
      return;
    }
    if (result.processed > 0) {
      setSyncState('done');
      window.setTimeout(() => {
        setSyncState('idle');
      }, 2800);
    } else {
      setSyncState('idle');
    }
  }, [refreshPendingCount]);

  useEffect(() => {
    void refreshPendingCount();
  }, [refreshPendingCount]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      void runSync();
    };
    const onOffline = () => {
      setIsOnline(false);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    setIsOnline(navigator.onLine);
    if (navigator.onLine) {
      void runSync();
    }
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [runSync]);

  const value = useMemo(
    () => ({
      isOnline,
      syncState,
      syncMessage,
      pendingCount,
      refreshPendingCount,
      runSync,
    }),
    [isOnline, syncState, syncMessage, pendingCount, refreshPendingCount, runSync],
  );

  return (
    <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>
  );
};

export const useOfflineSync = (): OfflineSyncContextValue => {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) {
    throw new Error('useOfflineSync must be used within OfflineSyncProvider');
  }
  return ctx;
};
